import { fetch } from 'http-common';

import { Constants, PostData } from 'types';
import { CustomerMapperPage } from 'types/api/api.type';
import { CustomerData } from 'types/api/customers.type';

import { SortItem } from 'components/Table/components/types';

/**
 * Envelope actually returned by the customer-mapper backend for every
 * create/update/get/submit/accept/refuse call — see customers.type.ts for
 * why the service flattens this into `CustomerData` before handing it back.
 */
export type CustomerRecord = {
  id: string;
  customerId: string;
  status: CustomerData['status'];
  timestamp: string;
  userId: string;
  customer: Omit<CustomerData, 'id' | 'userId' | 'status'> & {
    id: string;
    timestamp: string;
  };
};

export const toCustomerData = (record: CustomerRecord): CustomerData => ({
  ...record.customer,
  id: record.customerId,
  userId: record.userId,
  status: record.status,
});

export type StructureTypeData = {
  code: string;
  description: string;
};

class CustomersDataService {
  private http = fetch();

  private ep = '/v1/customer-mapper/customers';

  // The read-only CRM/order-creation `/v1/customers` module — unrelated to
  // Customer Mapper, only `getOrderCustomers` uses it.
  private crmEp = '/v1/customers';

  public async getAll(
    zone?: string,
    params?: URLSearchParams,
    page?: number,
    size?: number,
    sorting?: SortItem
  ) {
    const res = await this.http.get<CustomerMapperPage<CustomerRecord>>(this.ep, {
      params: {
        code: params?.get('code') || undefined,
        companyName: params?.get('companyName') || undefined,
        zone: zone || params?.get('zone') || undefined,
        status: params?.get('status') || undefined,
        page: page || 0,
        limit: size || 10,
        // Backend allowlist (customer-temp.repository.ts's SORTABLE_COLUMNS)
        // recognizes exactly these ids: code/zone/companyName/status —
        // same names as the table's accessors, so no id translation needed.
        sort: sorting ? `${sorting.id},${sorting.desc ? 'desc' : 'asc'}` : undefined,
      },
    });

    return {
      ...res,
      data: {
        ...res.data,
        content: res.data.content.map(toCustomerData),
      },
    };
  }

  public async get(id: string) {
    const res = await this.http.get<CustomerRecord>(`${this.ep}/${id}`);
    return { ...res, data: toCustomerData(res.data) };
  }

  public async post(data: PostData) {
    const res = await this.http.post<CustomerRecord>(this.ep, data);
    return { ...res, data: toCustomerData(res.data) };
  }

  public async put(data: PostData, id: string) {
    const res = await this.http.put<CustomerRecord>(`${this.ep}/${id}`, data);
    return { ...res, data: toCustomerData(res.data) };
  }

  /**
   * ADMIN-only, narrow "set the ERP code" action — works on ANY customer
   * regardless of ownership (unlike put(), which is ownership-gated) and
   * does not reset status or touch any other field. See
   * .claude/customer-mapper-context.md.
   */
  public async updateCode(id: string, code: string) {
    const res = await this.http.put<CustomerRecord>(`${this.ep}/${id}/code`, { code });
    return { ...res, data: toCustomerData(res.data) };
  }

  public async accept(id: string) {
    const res = await this.http.post<CustomerRecord>(`${this.ep}/${id}/accept`);
    return { ...res, data: toCustomerData(res.data) };
  }

  public async refuse(id: string) {
    const res = await this.http.post<CustomerRecord>(`${this.ep}/${id}/refuse`);
    return { ...res, data: toCustomerData(res.data) };
  }

  public async submit(id: string) {
    const res = await this.http.post<CustomerRecord>(`${this.ep}/${id}/submit`);
    return { ...res, data: toCustomerData(res.data) };
  }

  /** Soft delete — ADMIN-only on the backend. Appends a DELETED row, the
   * customer just disappears from getAll()'s default (unfiltered) list. */
  public async delete(id: string) {
    const res = await this.http.post<CustomerRecord>(`${this.ep}/${id}/delete`);
    return { ...res, data: toCustomerData(res.data) };
  }

  /**
   * Fixed list (mirrors the legacy Java `CustomerStructure` enum, not
   * DB-driven) — lives under the customer-mapper prefix, not `crmEp`,
   * since it's specific to Customer Mapper's `structure` field.
   */
  public async getStructureTypes() {
    return this.http.get<StructureTypeData[]>(`${this.ep}/structure-types`);
  }

  /**
   * Fixed Shop "analysis" dropdown lists (mirrors legacy Java `Shop*`
   * enums, not DB-driven) — flat `{ShopSeasons: [...], ShopRanking: [...],
   * ...}` shape, already matching `redux/slices/constants.ts`'s
   * `constants` state 1:1 (`Constant[]` per key), so callers can dispatch
   * the response straight into `setShopConstants` with no reshaping.
   */
  public async getShopConstants() {
    return this.http.get<Omit<Constants, 'CustomerStructure'>>(`${this.ep}/shop-constants`);
  }

  public async getOrderCustomers(zone: string) {
    const res = await this.http.get<{
      content: { customerCode: string; displayName: string }[];
      totalElements: number;
      totalPages: number;
    }>(`${this.crmEp}/search`, {
      params: {
        zone,
        status: 'APPROVED',
        size: 1000,
      },
    });

    return {
      ...res,
      data: {
        ...res.data,
        content: res.data.content.map(({ customerCode, displayName }) => ({
          code: customerCode,
          companyName: displayName,
        })),
      },
    };
  }
}

const customersDataService = new CustomersDataService();

export { customersDataService };
